import { Capacitor } from "@capacitor/core";

import {
  ANALYTICS_CONSENT_POLICY_VERSION,
  ANALYTICS_MAX_BATCH_EVENTS,
  AnalyticsClientContextSchema,
  ClientProductAnalyticsEventSchema,
  PROTOCOL_VERSION,
  type AnalyticsClientContext,
  type AnalyticsMatchContext,
  type ClientProductAnalyticsEvent,
  type ProductAnalyticsErrorCode,
} from "@pactfront/contracts";

import { serverUrl } from "./serverUrl.js";

export type AnalyticsConsent = "unset" | "granted" | "denied";
export type AnalyticsErrorSurface =
  "startup" | "landing" | "lobby" | "match" | "reconnect" | "tutorial" | "store" | "unknown";

interface LifecycleSnapshot {
  view: {
    phase: "planning" | "locked" | "resolving" | "playback" | "finished";
    turnNumber: number;
  } | null;
}

const consentKey = "pactfront.analyticsConsent.v1";
const subjectKey = "pactfront.analyticsSubject.v1";
const maxQueuedEvents = 100;
const maxBatchBytes = 64 * 1024;
const listeners = new Set<() => void>();
const queuedEvents: ClientProductAnalyticsEvent[] = [];
const emittedOnce = new Set<string>();
const errorCounts = new Map<ProductAnalyticsErrorCode, number>();
const clientSessionStartedAt = performance.now();
let consent = readConsent();
let analyticsSessionId: string | null = null;
let analyticsMatchContext: AnalyticsMatchContext | null = null;
let matchEntryKind: "start" | "resume" = "start";
let matchEntryStartedAt = performance.now();
let pendingMapReady = false;
let pendingMapReadyDurationMs: number | null = null;
let firstUsableMeasurement: {
  entrySurface: "landing" | "resume";
  landingVisible: boolean;
  durationMs: number;
} | null = null;
let reconnectCount = 0;
let fatalErrorCount = 0;
let peakMemoryMb: number | null = null;
let flushTimer: number | null = null;
let flushing = false;
let activeRequest: AbortController | null = null;
let lifecycleRegistered = false;

function readConsent(): AnalyticsConsent {
  const saved = localStorage.getItem(consentKey);
  return saved === "granted" || saved === "denied" ? saved : "unset";
}

function notifyConsentListeners(): void {
  for (const listener of listeners) listener();
}

export function getAnalyticsConsent(): AnalyticsConsent {
  return consent;
}

export function subscribeAnalyticsConsent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setAnalyticsConsent(next: Exclude<AnalyticsConsent, "unset">): void {
  if (consent === next) return;
  consent = next;
  localStorage.setItem(consentKey, next);
  if (next === "denied") {
    queuedEvents.splice(0);
    localStorage.removeItem(subjectKey);
    analyticsSessionId = null;
    analyticsMatchContext = null;
    pendingMapReady = false;
    pendingMapReadyDurationMs = null;
    emittedOnce.clear();
    errorCounts.clear();
    if (flushTimer !== null) window.clearTimeout(flushTimer);
    flushTimer = null;
    activeRequest?.abort();
    activeRequest = null;
  }
  notifyConsentListeners();
}

function analyticsSubjectId(): string {
  const stored = localStorage.getItem(subjectKey);
  if (
    stored !== null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(stored)
  )
    return stored;
  const created = crypto.randomUUID();
  localStorage.setItem(subjectKey, created);
  return created;
}

function currentSessionId(): string {
  analyticsSessionId ??= crypto.randomUUID();
  return analyticsSessionId;
}

function platform(): "web" | "android" | "ios" {
  const current = Capacitor.getPlatform();
  return current === "android" || current === "ios" ? current : "web";
}

function normalizedLocale(): string {
  const locale = document.documentElement.lang.toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(locale) ? locale : "en";
}

function networkClass(): "offline" | "slow" | "normal" | "unknown" {
  if (!navigator.onLine) return "offline";
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
    .connection;
  if (connection?.effectiveType === "4g") return "normal";
  if (
    connection?.effectiveType === "slow-2g" ||
    connection?.effectiveType === "2g" ||
    connection?.effectiveType === "3g"
  )
    return "slow";
  return "unknown";
}

function sampleMemory(): void {
  const memory = (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory;
  if (memory?.usedJSHeapSize === undefined) return;
  const current = Math.round((memory.usedJSHeapSize / 1_048_576) * 10) / 10;
  peakMemoryMb = peakMemoryMb === null ? current : Math.max(peakMemoryMb, current);
}

function analyticsContext(): AnalyticsClientContext {
  return AnalyticsClientContextSchema.parse({
    analyticsSubjectId: analyticsSubjectId(),
    analyticsSessionId: currentSessionId(),
    identityKind: "guest",
    platform: platform(),
    locale: normalizedLocale(),
    acquisitionSource: "direct",
    appVersion: "0.1.0",
    protocolVersion: PROTOCOL_VERSION,
  });
}

export function getAnalyticsAssociationContext(): AnalyticsClientContext | null {
  return consent === "granted" ? analyticsContext() : null;
}

function commonEventFields(includeMatch = false) {
  return {
    ...analyticsContext(),
    ...(includeMatch && analyticsMatchContext !== null ? analyticsMatchContext : {}),
    eventId: crypto.randomUUID(),
    eventVersion: 1 as const,
    occurredAt: new Date().toISOString(),
    source: "client" as const,
  };
}

function requestBody(events: readonly ClientProductAnalyticsEvent[]): string {
  return JSON.stringify({ consentPolicyVersion: ANALYTICS_CONSENT_POLICY_VERSION, events });
}

function nextBatch(): { events: ClientProductAnalyticsEvent[]; body: string } | null {
  const events = queuedEvents.slice(0, ANALYTICS_MAX_BATCH_EVENTS);
  while (events.length > 0) {
    const body = requestBody(events);
    if (new TextEncoder().encode(body).byteLength <= maxBatchBytes) return { events, body };
    events.pop();
  }
  queuedEvents.shift();
  return null;
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (flushing || consent !== "granted" || queuedEvents.length === 0) return;
  const batch = nextBatch();
  if (batch === null) return;
  flushing = true;
  const requestController = new AbortController();
  activeRequest = requestController;
  try {
    const response = await fetch(`${serverUrl}/api/analytics/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: batch.body,
      keepalive: true,
      signal: requestController.signal,
    });
    if (response.ok) queuedEvents.splice(0, batch.events.length);
  } catch {
    // Product analytics is best-effort and must not surface or block gameplay.
  } finally {
    if (activeRequest === requestController) activeRequest = null;
    flushing = false;
    if (queuedEvents.length > 0 && consent === "granted") scheduleFlush(5_000);
  }
}

function scheduleFlush(delay = 400): void {
  if (flushTimer !== null || consent !== "granted") return;
  flushTimer = window.setTimeout(() => void flush(), delay);
}

function enqueue(event: ClientProductAnalyticsEvent): void {
  if (consent !== "granted") return;
  const parsed = ClientProductAnalyticsEventSchema.safeParse(event);
  if (!parsed.success) return;
  if (queuedEvents.length >= maxQueuedEvents) queuedEvents.shift();
  queuedEvents.push(parsed.data);
  scheduleFlush();
}

function once(key: string, create: () => ClientProductAnalyticsEvent): void {
  if (consent !== "granted" || emittedOnce.has(key)) return;
  const event = create();
  emittedOnce.add(key);
  enqueue(event);
}

function roundedDuration(startedAt: number): number {
  return Math.min(86_400_000, Math.max(0, Math.round((performance.now() - startedAt) / 100) * 100));
}

export function recordUsableSurface(
  entrySurface: "landing" | "resume",
  landingVisible: boolean,
): void {
  firstUsableMeasurement ??= {
    entrySurface,
    landingVisible,
    durationMs: roundedDuration(clientSessionStartedAt),
  };
  sampleMemory();
  const measurement = firstUsableMeasurement;
  once("app_session_started", () => ({
    ...commonEventFields(),
    eventName: "app_session_started",
    properties: { entrySurface: measurement.entrySurface, launchKind: "cold" },
  }));
  once("first_usable_screen_measured", () => ({
    ...commonEventFields(),
    eventName: "first_usable_screen_measured",
    properties: {
      durationMs: measurement.durationMs,
      entrySurface: measurement.entrySurface,
      networkClass: networkClass(),
    },
  }));
  if (measurement.landingVisible) {
    once("landing_viewed", () => ({
      ...commonEventFields(),
      eventName: "landing_viewed",
      properties: { entrySurface: "landing" },
    }));
  }
}

export function beginAnalyticsMatchEntry(entryKind: "start" | "resume"): void {
  matchEntryKind = entryKind;
  matchEntryStartedAt = performance.now();
  pendingMapReady = false;
  pendingMapReadyDurationMs = null;
}

export function setAnalyticsMatchContext(context: AnalyticsMatchContext | null): void {
  analyticsMatchContext =
    consent !== "granted" || context === null ? null : structuredClone(context);
  if (analyticsMatchContext !== null && pendingMapReady) recordMapReady();
}

export function recordMapReady(): void {
  pendingMapReadyDurationMs ??= roundedDuration(matchEntryStartedAt);
  const durationMs = pendingMapReadyDurationMs;
  sampleMemory();
  if (analyticsMatchContext === null) {
    pendingMapReady = true;
    return;
  }
  pendingMapReady = false;
  once(`map_ready_measured:${analyticsMatchContext.analyticsMatchId}`, () => ({
    ...commonEventFields(true),
    eventName: "map_ready_measured",
    properties: {
      durationMs,
      entryKind: matchEntryKind,
      assetCache: "unknown",
    },
  }));
}

export function recordProductError(
  surface: AnalyticsErrorSurface,
  errorCode: ProductAnalyticsErrorCode,
  severity: "warning" | "recoverable" | "fatal" = "recoverable",
  recoverable = true,
): void {
  if (consent !== "granted") return;
  const count = errorCounts.get(errorCode) ?? 0;
  if (count >= 3) return;
  errorCounts.set(errorCode, count + 1);
  if (severity === "fatal") fatalErrorCount += 1;
  enqueue({
    ...commonEventFields(analyticsMatchContext !== null),
    eventName: "product_error_observed",
    properties: { surface, errorCode, severity, recoverable },
  });
}

export function recordReconnect(): void {
  reconnectCount += 1;
}

export function registerAnalyticsLifecycle(snapshot: () => LifecycleSnapshot): void {
  if (lifecycleRegistered) return;
  lifecycleRegistered = true;
  sampleMemory();
  const memoryTimer = window.setInterval(sampleMemory, 5_000);
  window.addEventListener("pageshow", () => {
    if (document.visibilityState === "visible") sampleMemory();
  });
  window.addEventListener("pagehide", () => {
    window.clearInterval(memoryTimer);
    sampleMemory();
    const current = snapshot();
    const view = current.view;
    if (view !== null && view.phase !== "finished" && analyticsMatchContext !== null) {
      once(`match_exit_observed:${analyticsMatchContext.analyticsMatchId}`, () => ({
        ...commonEventFields(true),
        eventName: "match_exit_observed",
        properties: {
          phase: view.phase === "planning" ? "planning" : "resolution",
          turnNumber: view.turnNumber,
          elapsedMs: roundedDuration(matchEntryStartedAt),
        },
      }));
    }
    once("client_health_summary", () => ({
      ...commonEventFields(),
      eventName: "client_health_summary",
      properties: {
        durationMs: roundedDuration(clientSessionStartedAt),
        peakMemoryMb,
        fatalErrorCount,
        reconnectCount,
      },
    }));
    if (consent === "granted") void flush();
  });
}
